import { createAction, createReducer } from '@reduxjs/toolkit';
import { aiRmvBgrndNoLimit } from 'reactApp/appHelpers/featuresHelpers/features/imageTools';
import type { IAiToolsState, ImageProcessRequestData, NewBackground, ProcessingType } from 'reactApp/modules/aiTools/aiTools.types';

const initialState: IAiToolsState = {
    processors: {},
    attemptsAvailable: 0,
    attemptsRmvBgrndAvailable: 0,
    startTime: 0,
    startTimeRmvBgrnd: 0,
};

export const aiToolsAttemptsInit = createAction('aiTools/aiToolsAttemptsInit');
export const aiToolsAttemptsSuccess = createAction<{
    attemptsAvailable: number;
    startTime?: number;
    type: ProcessingType;
}>('aiTools/aiToolsAttemptsSuccess');
export const improveImageRequest = createAction<ImageProcessRequestData>('aiTools/improveImageRequest');
export const improveImageSuccess = createAction<
    ImageProcessRequestData & {
        imageResult?: string;
    }
>('aiTools/improveImageSuccess');
export const updateImageSuccess = createAction<
    Omit<ImageProcessRequestData, 'ext'> & {
        imageResult?: string;
        isNewbackground?: boolean;
    }
>('aiTools/updateImageSuccess');
export const improveImageError = createAction<ImageProcessRequestData>('aiTools/improveImageError');
export const improveImageResultAction = createAction<
    ImageProcessRequestData & {
        result: 'save' | 'cancel' | 'done';
    }
>('aiTools/improveImageResultAction');
export const aiToolsCompareImage = createAction<
    ImageProcessRequestData & {
        compareRegime: boolean;
    }
>('aiTools/aiToolsCompareImage');
export const improveImageSave = createAction<
    ImageProcessRequestData & {
        saveAsNew: boolean;
        closeViewer?: boolean;
    }
>('aiTools/improveImageSave');
export const addNewBackground = createAction<NewBackground & { pathId: string }>('aiTools/addNewBackground');

export const aiToolsReducer = createReducer(initialState, {
    [aiToolsAttemptsSuccess.type]: (state, { payload }: ReturnType<typeof aiToolsAttemptsSuccess>) => {
        if (payload.type === 'improve') {
            state.startTime = payload.startTime || 0;
            state.attemptsAvailable = payload.attemptsAvailable;
        }
        if (payload.type === 'removeBackground' && !aiRmvBgrndNoLimit) {
            state.startTimeRmvBgrnd = payload.startTime || 0;
            state.attemptsRmvBgrndAvailable = payload.attemptsAvailable;
        }
    },
    [improveImageRequest.type]: (state, { payload }: ReturnType<typeof improveImageRequest>) => {
        state.processors[payload.id] = { state: 'processing', type: payload.type || 'improve' };
    },
    [improveImageSuccess.type]: (state, { payload }: ReturnType<typeof improveImageSuccess>) => {
        state.processors[payload.id] = {
            state: 'success',
            imageResult: payload.imageResult,
            type: state.processors[payload.id].type,
        };

        if (state.processors[payload.id].type === 'improve') {
            if (!state.startTime) {
                state.startTime = new Date().valueOf();
            }

            state.attemptsAvailable--;
        }

        if (state.processors[payload.id].type === 'removeBackground') {
            if (!state.startTimeRmvBgrnd) {
                state.startTimeRmvBgrnd = new Date().valueOf();
            }

            if (!aiRmvBgrndNoLimit) {
                state.attemptsRmvBgrndAvailable--;
            }
        }
    },
    [updateImageSuccess.type]: (state, { payload }: ReturnType<typeof updateImageSuccess>) => {
        const { id, imageResult, isNewbackground } = payload;
        if (state.processors[id]?.imageResult) {
            URL.revokeObjectURL(state.processors[id].imageResult as string);
        }
        state.processors[id].imageResult = imageResult;
        state.processors[id].isNewbackground = isNewbackground;
    },
    [improveImageError.type]: (state, { payload }: ReturnType<typeof improveImageError>) => {
        state.processors[payload.id] = { state: 'error', type: state.processors[payload.id]?.type };
    },
    [improveImageResultAction.type]: (state, { payload }: ReturnType<typeof improveImageResultAction>) => {
        if (payload.result === 'cancel' || payload.result === 'done') {
            const id = payload.id;
            if (state.processors[id]?.imageResult) {
                URL.revokeObjectURL(state.processors[id].imageResult as string);
            }
            state.processors[id] = {};
        }
    },
    [aiToolsCompareImage.type]: (state, { payload }: ReturnType<typeof aiToolsCompareImage>) => {
        const data = state.processors[payload.id];
        if (data.imageResult && (data.state === 'success' || data.state === 'compare')) {
            state.processors[payload.id].state = payload.compareRegime ? 'compare' : 'success';
        }
    },
});
